import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star, RotateCcw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CardPreview } from './CardPreview';
import { CandidateList } from './CandidateList';
import { API_URL, authHeaders, fmtWeek } from './potwApi';

export const PlayerOfWeekPanel = ({ eventId }) => {
  const ev = eventId && eventId !== 'all' ? eventId : '';
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState('');

  const call = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_URL}/api/stats/player-of-week${path}`, { ...opts, headers: { ...authHeaders(), ...opts.headers } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || 'Something went wrong');
    setData(body);
  }, []);

  const load = useCallback((week = '') => call(`?${new URLSearchParams({ event_id: ev, week })}`)
    .catch(e => toast.error(e.message)), [call, ev]);

  useEffect(() => { load(); }, [load]);

  const pick = async (c) => {
    setBusyKey(c.key);
    try {
      await call('/pick', { method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: ev, week_start: data.week_start, key: c.key }) });
      toast.success(`${c.name} is Player of the Week`);
    } catch (e) { toast.error(e.message); }
    setBusyKey('');
  };

  const reset = () => call(`/pick?${new URLSearchParams({ event_id: ev, week: data.week_start })}`, { method: 'DELETE' })
    .then(() => toast.success('Back to the suggested player'))
    .catch(e => toast.error(e.message));

  if (!data) {
    return <div className="py-16 flex justify-center"><div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!data.weeks.length) {
    return (
      <div className="text-center py-16" data-testid="potw-empty-state">
        <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Finish a game with stats to pick a Player of the Week.</p>
      </div>
    );
  }
  const picked = data.candidates.find(c => c.key === data.picked_key);

  return (
    <div className="space-y-6" data-testid="potw-panel">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>PLAYER OF THE WEEK</h2>
          <p className="text-sm text-slate-500 max-w-xl">We suggest the most efficient player from Monday to Sunday. Pick someone else any time, then download the image to post.</p>
        </div>
        <Select value={data.week_start} onValueChange={load}>
          <SelectTrigger className="w-full sm:w-[260px] h-10 bg-white" data-testid="potw-week-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {data.weeks.map(w => (
              <SelectItem key={w.week_start} value={w.week_start} data-testid={`potw-week-${w.week_start}`}>
                {fmtWeek(w.week_start)} · {w.games_count} game{w.games_count !== 1 ? 's' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6 items-start">
        <CardPreview eventId={ev} week={data.week_start} version={data.picked_key} playerName={picked?.name} />
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="potw-week-games">{data.games_count} game{data.games_count !== 1 ? 's' : ''} this week · ranked by efficiency</span>
            {data.is_override && (
              <button onClick={reset} className="inline-flex items-center gap-1 font-semibold text-[#00247D] hover:underline" data-testid="potw-reset-btn">
                <RotateCcw className="w-3 h-3" /> Use suggested
              </button>
            )}
          </div>
          <CandidateList candidates={data.candidates} pickedKey={data.picked_key} suggestedKey={data.suggested_key} onPick={pick} busyKey={busyKey} />
        </div>
      </div>
    </div>
  );
};
